import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Plus, Save, GripVertical, FileText, Video, ArrowLeft, Upload, Edit, Play } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Video Form State
  const [videoForm, setVideoForm] = useState({ title: '', vimeoId: '', hash: '', category: 'Servicio', order: 0 });
  const [editingVideoId, setEditingVideoId] = useState(null);

  // Doc Form State
  const [docForm, setDocForm] = useState({ title: '', url: '', type: 'PDF', order: 0 });
  const [editingDocId, setEditingDocId] = useState(null);
  
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('adminAuthenticated') !== 'true') {
      navigate('/');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vidsRes, docsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/videos`),
        axios.get(`${BACKEND_URL}/api/documents`)
      ]);
      setVideos(vidsRes.data);
      setDocuments(docsRes.data);
      
      if (!editingVideoId) setVideoForm(prev => ({ ...prev, order: vidsRes.data.length + 1 }));
      if (!editingDocId) setDocForm(prev => ({ ...prev, order: docsRes.data.length + 1 }));

    } catch (error) {
      toast.error("Failed to load data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // --- Helpers ---
  // Memoized parser for PREVIEW only
  const parsedVimeoData = useMemo(() => {
      let input = videoForm.vimeoId || '';
      input = input.trim();
      
      let id = input;
      let hash = videoForm.hash; 

      if (input.includes('vimeo.com')) {
          try {
              const url = new URL(input);
              const pathParts = url.pathname.split('/').filter(p => p);
              
              if (pathParts.length >= 1 && /^\d+$/.test(pathParts[0])) {
                  id = pathParts[0];
                  
                  if (pathParts.length >= 2) {
                      hash = pathParts[1];
                  } else {
                      const hParam = url.searchParams.get('h');
                      if (hParam) hash = hParam;
                  }
              }
          } catch (e) {
              console.error("Invalid URL", e);
          }
      }
      return { id, hash };
  }, [videoForm.vimeoId, videoForm.hash]);

  const getVimeoEmbedUrl = (vimeoId, hash) => {
      if (!vimeoId) return '';
      // If user types ID 123 -> https://player.vimeo.com/video/123
      if (!vimeoId.includes('http')) {
          let src = `https://player.vimeo.com/video/${vimeoId}`;
          if (hash) src += `?h=${hash}`;
          src += hash ? '&' : '?';
          src += 'title=0&byline=0&portrait=0';
          return src;
      }
      return ''; // Can't preview raw URL directly in iframe usually without conversion
  };

  // --- Videos ---
  const handleSaveVideo = async (e) => {
    e.preventDefault();
    
    // SAVE EXACTLY WHAT USER TYPED
    // We do NOT override vimeoId with parsedVimeoData.id anymore
    const payload = { ...videoForm };

    try {
      if (editingVideoId) {
        await axios.put(`${BACKEND_URL}/api/videos/${editingVideoId}`, payload);
        toast.success("Video updated");
        setEditingVideoId(null);
      } else {
        await axios.post(`${BACKEND_URL}/api/videos`, payload);
        toast.success("Video added");
      }
      fetchData();
      setVideoForm({ title: '', vimeoId: '', hash: '', category: 'Servicio', order: videos.length + 2 });
    } catch (err) {
      console.error(err);
      toast.error(`Failed to save video: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleEditVideo = (vid) => {
    setVideoForm(vid);
    setEditingVideoId(vid.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelVideoEdit = () => {
    setEditingVideoId(null);
    setVideoForm({ title: '', vimeoId: '', hash: '', category: 'Servicio', order: videos.length + 1 });
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/videos/${id}`);
      toast.success("Video deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete video");
    }
  };

  // --- Documents ---
  const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);
      
      setUploading(true);
      try {
          const res = await axios.post(`${BACKEND_URL}/api/upload`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
          });
          const relativeUrl = res.data.url; 
          setDocForm(prev => ({ ...prev, url: relativeUrl }));
          toast.success("File uploaded!");
      } catch (err) {
          toast.error("Upload failed");
      } finally {
          setUploading(false);
      }
  };

  const handleSaveDoc = async (e) => {
    e.preventDefault();
    try {
      if (editingDocId) {
        await axios.put(`${BACKEND_URL}/api/documents/${editingDocId}`, docForm);
        toast.success("Document updated");
        setEditingDocId(null);
      } else {
        await axios.post(`${BACKEND_URL}/api/documents`, docForm);
        toast.success("Document added");
      }
      fetchData();
      setDocForm({ title: '', url: '', type: 'PDF', order: documents.length + 2 });
    } catch (err) {
      console.error(err);
      toast.error(`Failed to save document: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleEditDoc = (doc) => {
    setDocForm(doc);
    setEditingDocId(doc.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelDocEdit = () => {
    setEditingDocId(null);
    setDocForm({ title: '', url: '', type: 'PDF', order: documents.length + 1 });
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/documents/${id}`);
      toast.success("Document deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleLogout = () => {
      localStorage.removeItem('adminAuthenticated');
      navigate('/');
  };

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-white">Loading Admin...</div>;

  return (
    <div className="min-h-screen bg-[#09090B] text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
                <Button variant="outline" size="icon" onClick={() => navigate('/')} className="border-zinc-800 hover:bg-zinc-800 text-white">
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-3xl font-bold uppercase tracking-tight">Admin Console</h1>
            </div>
            <Button variant="destructive" onClick={handleLogout}>Exit Admin</Button>
        </div>

        <Tabs defaultValue="videos" className="space-y-8">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="videos" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">Videos</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">Documents</TabsTrigger>
          </TabsList>

          {/* VIDEOS TAB */}
          <TabsContent value="videos" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800 text-white">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        {editingVideoId ? <Edit className="text-yellow-500" /> : <Plus className="text-red-500" />} 
                        {editingVideoId ? 'Edit Video' : 'Add New Video'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Form Column */}
                        <div className="lg:col-span-2">
                            <form onSubmit={handleSaveVideo} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Label>Title</Label>
                                        <Input value={videoForm.title} onChange={e => setVideoForm({...videoForm, title: e.target.value})} className="bg-black border-zinc-700" placeholder="Title" required />
                                    </div>
                                    <div className="col-span-2">
                                        <Label>Vimeo ID (or Paste Full URL)</Label>
                                        <Input 
                                            value={videoForm.vimeoId} 
                                            onChange={e => setVideoForm({...videoForm, vimeoId: e.target.value})} 
                                            className="bg-black border-zinc-700" 
                                            placeholder="123456 or https://vimeo.com/..." 
                                            required 
                                        />
                                        <p className="text-[10px] text-zinc-500 mt-1">
                                            The full URL will be saved. The system will extract the ID automatically for playback.
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Hash (Optional)</Label>
                                        <Input 
                                            value={videoForm.hash} 
                                            onChange={e => setVideoForm({...videoForm, hash: e.target.value})} 
                                            className="bg-black border-zinc-700" 
                                            placeholder="abc1234" 
                                        />
                                    </div>
                                    <div>
                                        <Label>Category</Label>
                                        <Input value={videoForm.category} onChange={e => setVideoForm({...videoForm, category: e.target.value})} className="bg-black border-zinc-700" placeholder="Servicio" required />
                                    </div>
                                    <div>
                                        <Label>Order</Label>
                                        <Input type="number" value={videoForm.order} onChange={e => setVideoForm({...videoForm, order: parseInt(e.target.value) || 0})} className="bg-black border-zinc-700" />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1" disabled={uploading}><Save className="mr-2 h-4 w-4" /> Save</Button>
                                    {editingVideoId && (
                                        <Button type="button" variant="outline" onClick={handleCancelVideoEdit} className="border-zinc-700">Cancel</Button>
                                    )}
                                </div>
                            </form>
                        </div>

                        {/* Preview Column */}
                        <div className="lg:col-span-1">
                            <Label className="mb-2 block text-zinc-400">Live Preview</Label>
                            <div className="aspect-video bg-black rounded-lg border border-zinc-800 overflow-hidden relative">
                                {parsedVimeoData.id ? (
                                    <iframe 
                                        src={getVimeoEmbedUrl(parsedVimeoData.id, parsedVimeoData.hash)}
                                        className="absolute top-0 left-0 w-full h-full" 
                                        frameBorder="0" 
                                        allow="autoplay; fullscreen; picture-in-picture" 
                                        allowFullScreen
                                        title="Preview"
                                    ></iframe>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
                                        <div className="text-center">
                                            <Play className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                            <span className="text-xs">Enter ID/URL to Preview</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <p className="text-xs text-zinc-500 mt-2">
                                Detected ID: <strong>{parsedVimeoData.id}</strong>
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="space-y-4">
                {videos.map((vid) => (
                    <div key={vid.id} className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg hover:border-red-900 transition-colors">
                        <GripVertical className="text-zinc-600 cursor-move" />
                        <div className="h-10 w-16 bg-black flex items-center justify-center rounded text-zinc-700">
                            <span className="text-xs font-bold text-zinc-500">#{vid.order}</span>
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold">{vid.title}</h3>
                            <p className="text-xs text-zinc-500 truncate max-w-md">{vid.vimeoId}</p>
                        </div>
                        <Badge variant="outline" className="border-zinc-700">{vid.category}</Badge>
                        <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEditVideo(vid)} className="text-zinc-500 hover:text-yellow-500 hover:bg-transparent">
                                <Edit className="h-5 w-5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteVideo(vid.id)} className="text-zinc-500 hover:text-red-500 hover:bg-transparent">
                                <Trash2 className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800 text-white">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        {editingDocId ? <Edit className="text-yellow-500" /> : <Plus className="text-red-500" />}
                        {editingDocId ? 'Edit Document' : 'Add New Document'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSaveDoc} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                        <div className="lg:col-span-2 space-y-2">
                            <Label>Title</Label>
                            <Input value={docForm.title} onChange={e => setDocForm({...docForm, title: e.target.value})} className="bg-black border-zinc-700" placeholder="Title" required />
                        </div>
                        
                        <div className="lg:col-span-2 space-y-2">
                             <Label>File URL or Upload</Label>
                             <div className="flex gap-2">
                                <Input value={docForm.url} onChange={e => setDocForm({...docForm, url: e.target.value})} className="bg-black border-zinc-700" placeholder="https://..." required />
                                <div className="relative">
                                    <Input type="file" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer" disabled={uploading} />
                                    <Button type="button" variant="outline" className="border-zinc-700" disabled={uploading}>
                                        <Upload className="h-4 w-4" />
                                    </Button>
                                </div>
                             </div>
                        </div>

                        <div className="space-y-2">
                             <Label>Type</Label>
                             <select 
                                className="flex h-10 w-full rounded-md border border-zinc-700 bg-black px-3 py-2 text-sm text-white"
                                value={docForm.type}
                                onChange={e => setDocForm({...docForm, type: e.target.value})}
                             >
                                <option value="PDF">PDF</option>
                                <option value="XLSX">XLSX</option>
                             </select>
                        </div>
                         <div className="space-y-2">
                             <Label>Order</Label>
                             <Input type="number" value={docForm.order} onChange={e => setDocForm({...docForm, order: parseInt(e.target.value) || 0})} className="bg-black border-zinc-700" />
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1" disabled={uploading}><Save className="mr-2 h-4 w-4" /> Save</Button>
                            {editingDocId && (
                                <Button type="button" variant="outline" onClick={handleCancelDocEdit} className="border-zinc-700">Cancel</Button>
                            )}
                        </div>
                    </form>
                </CardContent>
            </Card>

             <div className="space-y-4">
                {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg hover:border-red-900 transition-colors">
                        <GripVertical className="text-zinc-600 cursor-move" />
                        <div className="h-10 w-10 bg-black flex items-center justify-center rounded text-zinc-700">
                            <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-bold">{doc.title}</h3>
                            <p className="text-xs text-zinc-500 truncate max-w-md">{doc.url}</p>
                        </div>
                        <Badge variant="outline" className="border-zinc-700">{doc.type}</Badge>
                        <div className="flex items-center gap-1 text-xs text-zinc-400" title="Descargas registradas" data-testid={`admin-doc-downloads-${doc.id}`}>
                            <span className="font-bold text-white">{doc.download_count || 0}</span>
                            <span className="hidden sm:inline">descarga{(doc.download_count || 0) === 1 ? '' : 's'}</span>
                        </div>
                         <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEditDoc(doc)} className="text-zinc-500 hover:text-yellow-500 hover:bg-transparent">
                                <Edit className="h-5 w-5" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)} className="text-zinc-500 hover:text-red-500 hover:bg-transparent">
                                <Trash2 className="h-5 w-5" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
